import {groupByUniqueKey} from '@benchling/js-utils';

import {DataModificationsHeader} from 'audit/DataModifications';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {
  EditExistingTask,
  CreateOrUpdateServerProcessOutput,
  bulkCreateOrUpdateProcessOutputs,
} from 'processes/dao/ProcessManagementDAO';
import ProcessOutputStructuredTableAdapter, {
  ProcessOutputStructuredTable,
} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import ProcessTaskStructuredTableAdapter, {
  ProcessTaskStructuredTable,
} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import setRowsInTaskTable from 'processes/tables/setRowsInTaskTable';
import updateOutputTable from 'processes/tables/updateOutputTable';

import {ProcessOutputGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessOutputGraphQL';
import {ProcessTaskGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskGraphQL';
import {ProcessDetailsQuery} from './__generated/ProcessDetailsQuery';

type ProcessGql = ExtractByTypename<ProcessDetailsQuery['process'], 'ProcessNode'>;

export interface UpdatedOutputsTableWithResponse {
  newOutputTable: ProcessOutputStructuredTable;
  serverTasks: Array<ProcessTaskGraphQL>;
  serverOutputs: Array<ProcessOutputGraphQL>;
}

export async function submitOutputs({
  processOutputs,
  processTasks,
  outputTable,
  dataModsHeader,
}: {
  processOutputs: Array<CreateOrUpdateServerProcessOutput>;
  processTasks?: Array<EditExistingTask>;
  outputTable: ProcessOutputStructuredTable;
  dataModsHeader?: DataModificationsHeader;
}): Promise<UpdatedOutputsTableWithResponse> {
  const response = await bulkCreateOrUpdateProcessOutputs({
    processOutputs,
    processTasks,
    totalOutputsInTable: processOutputs.length,
    dataModsHeader,
  });
  const newOutputTable = updateOutputTable(
    outputTable,
    response.processOutputs,
    response.processOutputs.map((output) =>
      ProcessOutputStructuredTableAdapter.optimisticallyMakeLastSyncedUpstreamValuesFromOutput(
        output,
        outputTable.columns
      )
    )
  );
  return {
    newOutputTable,
    serverTasks: response.processTasks,
    serverOutputs: response.processOutputs,
  };
}

export function mergeTasksIntoTaskTable(
  taskTable: ProcessTaskStructuredTable,
  process: ProcessGql,
  serverTasks: Array<ProcessTaskGraphQL>
): ProcessTaskStructuredTable {
  const updatedTasksById = groupByUniqueKey(serverTasks, (task) => task.id);
  const updatedTasks = process.tasks.map((task) => updatedTasksById[task.id] ?? task);
  return setRowsInTaskTable(
    taskTable,
    updatedTasks,
    updatedTasks.map((task) =>
      ProcessTaskStructuredTableAdapter.optimisticallyMakeLastSyncedUpstreamValuesFromTask(
        task,
        taskTable.columns
      )
    ),
    true
  );
}
